import type { Brand } from '@@types'

const CACHE_KEY = 'brand-square-logo-cache-v1'
const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14 // 14 days

interface CacheEntry {
  url: string
  dataUrl: string
  updatedAt: number
}

type CacheStore = Record<string, CacheEntry>

const inflight: Map<string, Promise<string | null>> = new Map()

const hasStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const readCache = (): CacheStore => {
  if (!hasStorage()) return {}
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as CacheStore
    return parsed && typeof parsed === 'object' ? parsed : {}
  }
  catch (error) {
    console.warn('[brandIconCache] readCache failed', error)
    return {}
  }
}

const writeCache = (cache: CacheStore) => {
  if (!hasStorage()) return
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  }
  catch (error) {
    console.warn('[brandIconCache] writeCache failed', error)
  }
}

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

const fetchAsDataUrl = async (iconUrl: string): Promise<string> => {
  const response = await fetch(iconUrl, { mode: 'cors' })
  if (!response.ok) {
    throw new Error(`Failed to fetch brand icon: ${response.status}`)
  }
  const blob = await response.blob()
  return blobToDataUrl(blob)
}

export const getCachedBrandIcon = (brandId: string, iconUrl?: string | null): string | null => {
  if (!iconUrl) return null
  const cache = readCache()
  const cached = cache[brandId]
  if (!cached) return null

  const isExpired = Date.now() - cached.updatedAt > CACHE_TTL_MS
  if (cached.url !== iconUrl || isExpired) return null

  return cached.dataUrl || iconUrl
}

export const cacheBrandIcon = async (brandId: string, iconUrl: string): Promise<string | null> => {
  if (!hasStorage()) return iconUrl
  if (!brandId || !iconUrl) return null

  const key = `${brandId}-${iconUrl}`
  const existingPromise = inflight.get(key)
  if (existingPromise) return existingPromise

  const promise = (async () => {
    try {
      const dataUrl = await fetchAsDataUrl(iconUrl)
      const cache = readCache()
      cache[brandId] = {
        url: iconUrl,
        dataUrl,
        updatedAt: Date.now(),
      }
      writeCache(cache)
      return dataUrl
    }
    catch (error) {
      console.warn('[brandIconCache] cacheBrandIcon failed', error)
      return iconUrl
    }
    finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)
  return promise
}

export const prefetchBrandIcons = (brands: (Brand | null | undefined)[]) => {
  if (!hasStorage()) return

  brands.forEach((brand) => {
    const brandId = brand?.id ? String(brand.id) : null
    const iconUrl = brand?.squareLogo?.url

    if (!brandId || !iconUrl) return
    const cached = getCachedBrandIcon(brandId, iconUrl)
    if (cached) return

    void cacheBrandIcon(brandId, iconUrl)
  })
}
